/**
 * LiverKidneyTwinPage — iter629 — Чернодробно-бъбречен близнак (16-ти).
 */
import MedModuleNotes from "@/components/meds/MedModuleNotes";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Droplet, Plus, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function LiverKidneyTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    ast_u_l: "", alt_u_l: "", ggt_u_l: "",
    creatinine_umol_l: "", egfr_ml_min: "", urea_mmol_l: "", alcohol_g_per_week: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/liver-kidney-twin/snapshot`);
      setSnap(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "").map(([k, v]) => [k, parseFloat(v)])
      );
      if (Object.keys(payload).length === 0) { toast.error("Попълни поне едно поле."); return; }
      await axios.post(`${API}/liver-kidney-twin/log`, payload);
      window.dispatchEvent(new Event("liver-kidney-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success("Записано!");
      setForm({ ast_u_l: "", alt_u_l: "", ggt_u_l: "", creatinine_umol_l: "", egfr_ml_min: "", urea_mmol_l: "", alcohol_g_per_week: "" });
      load();
    } catch { toast.error("Грешка."); }
    finally { setSaving(false); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="lk-twin-page">
      <MobileTopBar title="Черен дроб" fallbackTo="/dashboard" testid="liver_kidney-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Към таблото</button>
        <div className="flex items-center gap-2 mb-3">
          <Droplet className="w-5 h-5 text-blue-500" />
          <h1 className="text-xl font-bold">{twinLabel("liver_kidney")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>ALT, AST и GGT за черния дроб. Скоростта на филтрация и креатининът се оценяват в „Здраве на бъбреците“. Данните се вземат автоматично от качените резултати.</p>

        <button
          onClick={() => navigate("/dashboard/kidney-intelligence")}
          data-testid="lk-open-khi-btn"
          className={`w-full mb-4 p-3 rounded-2xl border-2 text-left transition-colors flex items-center gap-3 ${
            isDark ? "border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20" : "border-cyan-300 bg-cyan-50 hover:bg-cyan-100"
          }`}
        >
          <HeartPulse className="w-5 h-5 text-cyan-500 shrink-0" />
          <div>
            <div className="text-sm font-bold">Отвори Kidney Health Intelligence →</div>
            <div className="text-[11px] opacity-70">CKD скор, KDIGO лимити, дневен food-check и ренално седмично меню</div>
          </div>
        </button>

        {loading ? <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto my-12" /> : (
          <>
            <div className="mb-4">
              <MedModuleNotes module="liver" cardCls={cardCls} isDark={isDark} />
            </div>

            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="lk-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="lk-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
              </div>
              <p className="text-sm mt-2" data-testid="lk-interpretation">{snap?.interpretation_bg}</p>
            </div>

            <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="lk-log-form">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Ръчен лог</h2>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { k: "alt_u_l", label: "ALT (U/L)" },
                  { k: "ast_u_l", label: "AST (U/L)" },
                  { k: "ggt_u_l", label: "GGT (U/L)" },
                  { k: "creatinine_umol_l", label: "Креатинин (μmol/L)" },
                  { k: "egfr_ml_min", label: "eGFR (mL/min)" },
                  { k: "urea_mmol_l", label: "Урея (mmol/L)" },
                  { k: "alcohol_g_per_week", label: "Алкохол (g/седм.)" },
                ].map(f => (
                  <label key={f.k} className="flex flex-col gap-1">
                    <span className="text-[10px] opacity-70">{f.label}</span>
                    <input type="number" step="0.1" value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`lk-input-${f.k}`} className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
                  </label>
                ))}
              </div>
              <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="lk-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Запиши
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
